import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiTrendingUp } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useFormatters } from '../../lib/format'
import { useRentReviews, type RentReviewProposal } from '../../api/leaseRent'
import { RentReviewDialog } from './RentReviewDialog'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'outline'> = {
  proposed: 'secondary',
  accepted: 'success',
  rejected: 'outline',
  frozen: 'warning',
  foreclosed: 'warning',
}

function scrollTo(testId: string) {
  document.querySelector(`[data-testid="${testId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

/** The rent reviews of a lease, on its « Échéancier du loyer » tab (spec 2026-10-25-08 step 9). */
export function RentReviewsSection({ org, contractUid }: { org: string; contractUid: string }) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const { data } = useRentReviews(org, contractUid)
  const [open, setOpen] = useState<{ proposal?: RentReviewProposal } | null>(null)
  const proposals = data?.proposals ?? []

  const close = () => setOpen(null)
  const goTo = (testId: string) => {
    close()
    setTimeout(() => scrollTo(testId), 50)
  }

  return (
    <Card data-testid="rent-reviews-section">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{t('lease.rent_review.title')}</CardTitle>
        {data && (
          <Button size="sm" onClick={() => setOpen({})} data-testid="rent-review-start-button">
            <AppIcon path={mdiTrendingUp} size="sm" className="mr-2" />{t('lease.rent_review.start')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="rent-reviews-empty">{t('lease.rent_review.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('lease.rent_review.review_date')}</TableHead>
                <TableHead>{t('lease.rent_review.mechanism')}</TableHead>
                <TableHead>{t('lease.rent_review.status')}</TableHead>
                <TableHead className="text-right">{t('lease.rent_review.current_rent')}</TableHead>
                <TableHead className="text-right">{t('lease.rent_review.proposed_rent')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((p) => (
                <TableRow key={p.uid} className="cursor-pointer" onClick={() => setOpen({ proposal: p })} data-testid={`rent-review-row-${p.uid}`}>
                  <TableCell>{formatDate(p.review_date)}</TableCell>
                  <TableCell>{t(`lease.rent_review.mechanisms.${p.mechanism}` as any)}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[p.status] ?? 'outline'} data-testid={`rent-review-row-status-${p.uid}`}>
                      {t(`lease.rent_review.kinds.${p.status}` as any)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(p.current_rent_minor, p.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.proposed_rent_minor !== null && p.proposed_rent_minor !== undefined ? formatMoney(p.proposed_rent_minor, p.currency) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {open && data && (
        <RentReviewDialog
          org={org}
          contractUid={contractUid}
          shown={open.proposal ? { proposal: open.proposal } : { outcome: data.next }}
          buildingUid={data.building_uid}
          onClose={close}
          onShowReviewTerms={() => goTo('review-terms-card')}
          onShowAnchor={() => goTo('rent-schedule-tab')}
        />
      )}
    </Card>
  )
}
